import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import {
  WidgetCustomizationAction,
  WidgetCustomizationActionCreateAttributes,
} from "~/api/Api";
import MiddleTruncateTextPx from "~/components/dashboard/PageTable/MiddleTruncateUrl";
import {
  formatTableDate,
  isAiInstructionsCustomization,
  isRtl,
  isSchemaCustomization,
  isTitleOrDescriptionCustomization,
} from "~/helpers/helpers";
import { JsonParseError, isJsonString, jsonParseError } from "~/helpers/json";

import { apiClient } from "../../api/ApiClient";
import { applyPageMeta } from "../../api/apiFunctions";
import { queryKeys } from "../../api/queryKeys";
import { Icon } from "../../assets/icons/Icon";
import { messageFromError } from "../../helpers/messageFromError";
import Button from "../mantine/button/Button";
import Modal from "../ui/modal/Modal";
import Spinner from "../ui/spinner/Spinner";
import styles from "./ContentItem.module.scss";
import { ContentItem as ContentItemType } from "./ContentItems";
import MarkdownContent from "./MarkdownContentLazy";
import ContentSchemaModal from "./modals/ContentSchemaModal";
import VersionsModal from "./modals/VersionsModal";

type ContentItemProps = {
  item: ContentItemType;
  index?: number;
  approve: (
    data: WidgetCustomizationActionCreateAttributes,
  ) => Promise<WidgetCustomizationAction> | void;
  group?: "enhancement" | "ai" | "schema" | "ai_instructions";
  pendingStatuses?: WidgetCustomizationAction[];
  isPendingAprove?: boolean;
  approvePermission?: boolean;
  isLoading?: boolean;
  pageId: number;
  setCurrentItem?: (item: ContentItemType[]) => void;
  wrapperWidth: number;
  canDestroy?: boolean;
  canUpdate?: boolean;
};

const ContentItem = ({
  item,
  approve,
  group = "ai",
  pendingStatuses = [],
  isPendingAprove,
  approvePermission,
  isLoading,
  pageId,
  setCurrentItem,
  wrapperWidth,
  canDestroy = false,
  canUpdate = true,
}: ContentItemProps) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [isCopied, setIsCopied] = useState(false);
  const [isLocalPending, setIsLocalPending] = useState(false);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isVersionsModalOpen, setIsVersionsModalOpen] =
    useState<boolean>(false);
  const [editedSuggestion, setEditedSuggestion] = useState<{
    content: string;
    active: boolean;
  }>({ content: "", active: false });
  const [jsonError, setJsonError] = useState<JsonParseError | null>(null);
  const [isEditorLoading, setIsEditorLoading] = useState(false);
  const [editItem, setEditItem] = useState<ContentItemType | null>(null);
  const isTitleOrDescription = isTitleOrDescriptionCustomization(item);
  const isSchema = isSchemaCustomization(item);
  const isAiInstructions = isAiInstructionsCustomization(item);
  // ai.md keeps its markdown in `content`, but its <link> tag is "contentless"
  // so the API reports `contentful: false`. Treat it as content-bearing so the
  // body reads/writes the `content` column instead of `attrs.content`.
  const usesContent = Boolean(item.contentful) || isAiInstructions;
  const { mutate: editSuggestion, isPending: isPendingEditSuggestion } =
    useMutation({
      mutationFn: async (data: {
        content?: string;
        suggestion?: string;
        attrs?: Record<string, string>;
      }) => {
        const response =
          await apiClient.sites.widgetCustomizationsPartialUpdate(
            Number(id),
            item.id ?? 0,
            data,
          );

        return response.data;
      },
      onSuccess: async () => {
        enqueueSnackbar("Suggestion Updated", { variant: "success" });
        await queryClient.invalidateQueries({
          queryKey: ["contentAutomations", group, pageId],
        });
      },
      onError: (error) => {
        enqueueSnackbar(messageFromError(error), {
          variant: "error",
          persist: true,
        });
      },
    });
  const { mutate: applyFreshMutation, isPending: isApplyingFresh } =
    useMutation({
      mutationFn: ({ pageMetaId }: { pageMetaId: number }) =>
        applyPageMeta(Number(id), pageId, pageMetaId),
      onSuccess: async () => {
        enqueueSnackbar("Latest version applied successfully!", {
          variant: "success",
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.contentAutomationsRoot(group, pageId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.widgetCustomizationsStatsRoot(group),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.customizedPagesRoot(group),
          }),
          // Refresh the versions list so the newly applied version shows as
          // current next time the modal opens (all kinds for this page).
          queryClient.invalidateQueries({
            queryKey: queryKeys.pageMetasRoot(Number(id), pageId),
          }),
        ]);
      },
      onError: (error) => {
        enqueueSnackbar(messageFromError(error), {
          variant: "error",
          persist: true,
        });
      },
    });
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { mutate: deleteCustomization, isPending: isPendingDelete } =
    useMutation({
      mutationFn: async () => {
        await apiClient.sites.widgetCustomizationsDelete(
          Number(id),
          item.id ?? 0,
        );
      },
      onSuccess: async () => {
        enqueueSnackbar("Customization deleted", { variant: "success" });
        setIsDeleteConfirmOpen(false);

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["contentAutomations", group, pageId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["widgetCustomizationsStats", group],
          }),
          queryClient.invalidateQueries({
            queryKey: ["customizedPages", group],
          }),
        ]);
      },
      onError: (error) => {
        enqueueSnackbar(messageFromError(error), {
          variant: "error",
          persist: true,
        });
      },
    });

  useEffect(() => {
    if (!isPendingAprove && !isPendingEditSuggestion) {
      setIsEditModalOpen(false);
    }
  }, [isPendingAprove, isPendingEditSuggestion]);

  const wasClicked = useRef(false);

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content || "");
      setIsCopied(true);
    } catch (err) {
      console.error(err);
      setIsCopied(false);
    }
  };
  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    }
  }, [isCopied]);

  // A failed-validation error is stale as soon as the user edits the JSON.
  useEffect(() => {
    setJsonError(null);
  }, [editedSuggestion.content]);

  const openEditModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEditItem(item);

    let content = "";
    if (usesContent) {
      content = item.content || "";
    } else if (!item?.tag && item?.suggestion) {
      content = item.suggestion || "";
    } else {
      content = item?.attrs?.content || "";
    }

    setEditedSuggestion({
      content: content,
      active: !!item?.active,
    });

    setJsonError(null);
    setIsEditModalOpen(true);
  };

  const openVersionsModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsVersionsModalOpen(true);
  };

  const formatContent = (text: string | null | undefined) => {
    if (!text) return null;

    if (isJsonString(text)) {
      try {
        const jsonData = JSON.parse(text);

        return (
          <pre className={styles.jsonContent}>
            <code>{JSON.stringify(jsonData, null, 2)}</code>
          </pre>
        );
      } catch {
        return text;
      }
    }

    return text;
  };

  const renderContent = () => {
    if (isAiInstructions) {
      return (
        <MarkdownContent
          content={item.content || ""}
          rtl={rtl}
          lang={item.lang}
        />
      );
    }

    return formatContent(usesContent ? item.content : item?.attrs?.content);
  };

  const renderFreshContent = () => {
    const fresh = item.fresh_page_meta;
    if (!fresh) return null;

    if (isAiInstructions) {
      return (
        <MarkdownContent
          content={fresh.content || ""}
          rtl={rtl}
          lang={item.lang}
        />
      );
    }

    return formatContent(usesContent ? fresh.content : fresh.attrs?.content);
  };

  const handleApprove = (item: ContentItemType, event: React.MouseEvent) => {
    event.stopPropagation();

    // Suppress tooltip to prevent it from jumping to another button after re-render
    setTooltipSuppressed(true);
    setTimeout(() => setTooltipSuppressed(false), 600);

    if (approve && item?.id !== undefined) {
      setIsLocalPending(true);
      wasClicked.current = true;

      const result = approve({
        kind: item?.active ? "unapprove" : "approve",
        variant: group || "",
        ids: [item.id],
      });

      if (result instanceof Promise) {
        result.finally(() => {
          setTimeout(() => {
            setIsLocalPending(false);
            wasClicked.current = false;
          }, 500);
        });
      } else {
        setTimeout(() => {
          setIsLocalPending(false);
          wasClicked.current = false;
        }, 500);
      }
    }
  };

  const isItemPending =
    isLocalPending ||
    pendingStatuses.some(
      (status) =>
        status.status === "pending" &&
        status.ids &&
        Array.isArray(status.ids) &&
        status.ids.includes(item.id || -1),
    );
  const renderActionButton = () => {
    if (
      isItemPending ||
      (isPendingAprove && wasClicked.current) ||
      (isLoading && wasClicked.current)
    ) {
      return (
        <Button variant="nohoverWithPadding">
          <Spinner size="1rem" />
        </Button>
      );
    }

    return (
      <Button
        variant="nohoverWithPadding"
        onClick={(e) => handleApprove(item, e)}
        disabled={
          !canUpdate ||
          isItemPending ||
          isPendingAprove ||
          !approvePermission ||
          pendingStatuses?.length > 0
        }
        data-tooltip-id={`tooltip-${item.id}`}
        data-tooltip-content={item?.active ? "Unapprove" : "Approve"}
      >
        <Icon
          iconName={item?.active ? "GreenCheckmarkIcon" : "CloseIconCircled"}
          width="1rem"
          height="1rem"
        />
      </Button>
    );
  };
  // Validation runs on Update click only (not while typing): show the parse
  // error under the editor and keep the modal open instead of submitting.
  const handleModalSubmit = () => {
    if (isSchema) {
      const error = jsonParseError(editedSuggestion.content);
      if (error) {
        setJsonError(error);

        return;
      }
      setJsonError(null);
    }

    if (
      editedSuggestion.active !== item.active &&
      approve &&
      item?.id !== undefined
    ) {
      approve({
        kind: editedSuggestion.active ? "approve" : "unapprove",
        variant: group || "",
        ids: [item.id],
      });
    }

    if (usesContent) {
      if (item.content !== editedSuggestion.content) {
        editSuggestion({
          content: editedSuggestion.content,
        });
      }
    } else if (item.tag === "meta") {
      if (item?.attrs?.content !== editedSuggestion.content) {
        editSuggestion({
          attrs: {
            ...item.attrs,
            content: editedSuggestion.content,
          },
        });
      }
    } else if (!item?.tag && item?.suggestion) {
      if (item.suggestion !== editedSuggestion.content) {
        editSuggestion({
          suggestion: editedSuggestion.content,
        });
      }
    } else {
      if (item?.attrs?.content !== editedSuggestion.content) {
        editSuggestion({
          attrs: {
            ...item.attrs,
            content: editedSuggestion.content,
          },
        });
      }
    }
  };

  const rtl =
    isRtl(item.lang) &&
    (isTitleOrDescription ||
      isAiInstructions ||
      item.provider === "enhancement");

  return (
    <>
      <div className={styles.currentItem}>
        <div className={styles.currentItemHeader}>
          <div className={styles.currentItemHeaderTitle}>
            <div className={styles.currentItemHeaderTitleText}>
              <MiddleTruncateTextPx
                text={item?.title || ""}
                maxWidth={wrapperWidth - 190}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              {item.source && (
                <span
                  className={clsx(
                    styles.sourceBadge,
                    item.source === "auto" ? styles.auto : styles.manual,
                  )}
                >
                  {item.source === "auto" ? "Auto" : "Manual"}
                </span>
              )}
            </div>

            <div className={styles.currentItemHeaderIcons}>
              {(isTitleOrDescription || isSchema || isAiInstructions) && (
                <Button
                  variant="nohoverWithPadding"
                  onClick={openVersionsModal}
                  data-tooltip-id={`tooltip-${item.id}`}
                  data-tooltip-content="Versions"
                >
                  <Icon iconName="VersionsIcon" width="1rem" height="1rem" />
                </Button>
              )}
              <Button
                variant="nohoverWithPadding"
                onClick={openEditModal}
                disabled={!canUpdate}
                data-tooltip-id={`tooltip-${item.id}`}
                data-tooltip-content="Edit"
              >
                <Icon iconName="EditIcon" width="1rem" height="1rem" />
              </Button>
              {canDestroy && (
                <Button
                  variant="nohoverWithPadding"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteConfirmOpen(true);
                  }}
                  disabled={isPendingDelete}
                  data-tooltip-id={`tooltip-${item.id}`}
                  data-tooltip-content="Delete"
                >
                  {isPendingDelete ? (
                    <Spinner size="1rem" />
                  ) : (
                    <Icon iconName="TrashIcon" width="1rem" height="1rem" />
                  )}
                </Button>
              )}
              {renderActionButton()}
            </div>
          </div>
        </div>

        <div className={styles.currentItemContent}>
          <div className={styles.currentItemHeaderTitle}>
            <div>Suggestion</div>
            {isJsonString(
              usesContent ? item.content : item?.attrs?.content,
            ) && (
              <Button
                variant="nohoverWithPadding"
                onClick={() =>
                  copyToClipboard(
                    usesContent
                      ? item.content || ""
                      : item?.attrs?.content || "",
                  )
                }
              >
                {/*
                 // @ts-expect-error TS2339 non-existing className */}
                <span className={styles.buttonText}>
                  <Icon iconName={isCopied ? "CheckmarkIcon" : "CopyIcon"} />
                </span>
              </Button>
            )}
          </div>
          <div
            lang={item.lang || undefined}
            className={clsx(styles.currentItemContentDescription, {
              ["rtl"]: rtl,
            })}
          >
            {renderContent()}
            {!item?.tag && <div>{item?.suggestion}</div>}
          </div>

          {item.outdated && item.fresh_page_meta && (
            <div className={styles.freshVersion}>
              <div className={styles.freshHeading}>
                <span>
                  New update ({formatTableDate(item.fresh_page_meta.created_at)}
                  )
                </span>
                <Button
                  variant="outlinedIconSmall"
                  className={styles.freshApplyButton}
                  onClick={() =>
                    applyFreshMutation({
                      pageMetaId: item.fresh_page_meta!.id,
                    })
                  }
                  disabled={!canUpdate || isApplyingFresh}
                >
                  {isApplyingFresh && <Spinner size="0.8rem" />}
                  Apply
                </Button>
              </div>
              <div
                lang={item.lang || undefined}
                className={clsx(styles.currentItemContentDescription, {
                  ["rtl"]: rtl,
                })}
              >
                {renderFreshContent()}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isLoading={isPendingAprove || isPendingEditSuggestion}
        isOpen={isEditModalOpen}
        key="edit-suggestion-modal"
        closeModal={() => setIsEditModalOpen(false)}
        buttonLabel="Update"
        isDisabled={isEditorLoading}
        formId="contactDeveloper"
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>Update {editItem?.title}</h3>
        }
        action={handleModalSubmit}
      >
        <ContentSchemaModal
          setEditedSuggestion={setEditedSuggestion}
          editedSuggestion={editedSuggestion}
          rtl={rtl}
          lang={item.lang ?? null}
          isJson={isSchema}
          jsonError={jsonError}
          isEditorLoading={isEditorLoading}
          onEditorLoadingChange={setIsEditorLoading}
        />
      </Modal>

      {(isTitleOrDescription || isSchema || isAiInstructions) && (
        <VersionsModal
          isOpen={isVersionsModalOpen}
          onClose={() => setIsVersionsModalOpen(false)}
          item={item}
          group={group}
          pageId={pageId}
          setCurrentItem={setCurrentItem}
        />
      )}

      <Modal
        isOpen={isDeleteConfirmOpen}
        closeModal={() => setIsDeleteConfirmOpen(false)}
        buttonLabel="Delete"
        action={() => deleteCustomization()}
        isLoading={isPendingDelete}
        modalLabel={<h3>Delete Customization</h3>}
      >
        <p>
          Are you sure you want to delete <strong>{item.title}</strong>?
        </p>
      </Modal>

      {!tooltipSuppressed && (
        <Tooltip
          id={`tooltip-${item.id}`}
          delayShow={200}
          openEvents={{ mouseenter: true, focus: false }}
          closeEvents={{ mouseleave: true, click: true, blur: true }}
          globalCloseEvents={{ clickOutsideAnchor: true }}
          style={{
            borderRadius: "var(--border-radius)",
            font: "var(--ui-text-12-medium)",
          }}
        />
      )}
    </>
  );
};

export default ContentItem;
